import { useState } from "react";

import type { Job, JobError, JobProgress, Trigger } from "@/lib/jobs";

import { ScheduleEditor } from "./ScheduleEditor";
import { SettingsEditor } from "./SettingsEditor";
import { TallyCard } from "./TallyCard";
import {
  actionVerb,
  elapsedLabel,
  jobsSharingTime,
  jobsSummary,
  lastRunLabel,
  nextColumn,
  percent,
  queuedDetail,
  queuedLabel,
  runningLabel,
  runSummary,
  triggerLabel,
} from "./schedule";
import { Panel, PanelButton } from "./Setup";

/**
 * Jobs — `5e`. Replaces the Pipeline panel.
 *
 * **One row component for every state.** That is the load-bearing decision in the design: a
 * schedule is a property of a job, not a category of job, so there is no manual list and scheduled
 * list, and adding a schedule to a job does not move it anywhere.
 */
export function JobsPanel({
  jobs,
  live,
  errors,
  onRun,
  onCancel,
  onRetry,
  onSaveTrigger,
  onSaveSettings,
  onError,
  onOpenHistory,
  onOpenCard,
}: {
  jobs: Job[];
  live: JobProgress | null;
  errors: Record<number, JobError[]>;
  onRun: (id: string) => void;
  onCancel: (id: string) => void;
  onRetry: (id: string) => void;
  onSaveTrigger: (id: string, trigger: Trigger) => Promise<unknown>;
  onSaveSettings: (id: string, values: Record<string, number>) => Promise<unknown>;
  onError: (error: unknown) => void;
  onOpenHistory: () => void;
  onOpenCard: (noteId: number) => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <Panel
      title="ジョブ"
      subtitle={jobsSummary(jobs)}
      actions={<PanelButton onClick={onOpenHistory}>実行履歴</PanelButton>}
    >
      <div className="flex items-center gap-3 border-b border-line-soft px-4 py-[9px]">
        <span className="w-2 shrink-0" />
        <ColumnLabel className="flex-1">ジョブ</ColumnLabel>
        <ColumnLabel className="w-[186px] shrink-0">トリガー</ColumnLabel>
        <ColumnLabel className="w-[200px] shrink-0">前回</ColumnLabel>
        <ColumnLabel className="w-[104px] shrink-0">次回</ColumnLabel>
        <span className="w-[92px] shrink-0" />
      </div>

      {/* The order is the SERVER's — running, then the queue in the order it will run, then what the
          clock reaches next. Reading order is run order, which is why this maps rather than sorts. */}
      <p className="border-b border-line-soft px-4 py-1.5 text-meta text-ink-4">
        次回実行順、次に優先度順 — 表示順が実行順
      </p>

      {jobs.map((job) => (
        <JobRow
          key={job.id}
          job={job}
          jobs={jobs}
          live={live?.jobId === job.id ? live : null}
          errors={job.lastRun ? (errors[job.lastRun.id] ?? []) : []}
          // The disclosure opens the *schedule editor*. A failed row's errors are not behind it —
          // they show unasked, because closing a failure behind a chevron is how it gets forgotten.
          open={expanded === job.id}
          onToggle={() => setExpanded(expanded === job.id ? null : job.id)}
          onRun={() => onRun(job.id)}
          onCancel={() => onCancel(job.id)}
          onRetry={() => onRetry(job.id)}
          onSaveTrigger={(trigger) => onSaveTrigger(job.id, trigger)}
          onSaveSettings={(values) => onSaveSettings(job.id, values)}
          onError={onError}
          onOpenCard={onOpenCard}
        />
      ))}
    </Panel>
  );
}

function ColumnLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={`text-field-label font-semibold uppercase tracking-[.05em] text-ink-4 ${className ?? ""}`}
    >
      {children}
    </span>
  );
}

function JobRow({
  job,
  jobs,
  live,
  errors,
  open,
  onToggle,
  onRun,
  onCancel,
  onRetry,
  onSaveTrigger,
  onSaveSettings,
  onError,
  onOpenCard,
}: {
  job: Job;
  /** The whole list, for the schedule editor's two hints — how many jobs share this time, and
   *  whether this one's number collides. Both are questions about the set, not about the row. */
  jobs: Job[];
  live: JobProgress | null;
  errors: JobError[];
  open: boolean;
  onToggle: () => void;
  onRun: () => void;
  onCancel: () => void;
  onRetry: () => void;
  onSaveTrigger: (trigger: Trigger) => Promise<unknown>;
  onSaveSettings: (values: Record<string, number>) => Promise<unknown>;
  onError: (error: unknown) => void;
  onOpenCard: (noteId: number) => void;
}) {
  const running = job.state === "running";
  const queued = job.state === "queued";
  const failed = job.state === "failed";
  // A queued row is deliberately untinted. The two tints mean "something is happening here" and
  // "something went wrong here"; waiting is neither, and the hollow dot plus the status line say so.
  const tint = failed ? "bg-accent-ground" : running ? "bg-panel" : "";

  return (
    <div className={`border-b border-line-soft last:border-b-0 ${tint}`}>
      <div className="flex items-center gap-3 px-4 py-[11px]">
        <StateDot job={job} />

        <button
          type="button"
          onClick={onToggle}
          data-check={`setup.job.${job.id}`}
          className="flex min-w-0 flex-1 flex-col gap-[3px] text-left"
        >
          <span className="text-[15px]/[1.3] font-semibold text-ink">{job.name}</span>
          <span className="text-secondary text-ink-3">{job.description}</span>
        </button>

        <span className="flex w-[186px] shrink-0 items-center gap-2">
          {job.trigger.mode === "manual" ? (
            <span className="text-secondary text-ink-3">手動</span>
          ) : (
            <>
              <span className="inline-flex items-center rounded-[var(--r-4)] bg-accent-tint px-[9px] py-1 text-meta text-accent-text">
                {triggerLabel(job.trigger)}
              </span>
              {/* Beside the pill rather than inside it: the pill answers *when*, this answers *in
                  what order among the jobs sharing that when*. Boxed in red on a conflict, on BOTH
                  rows — marking only one of them would read as naming a culprit, and neither is. */}
              <span
                className={
                  job.blocked
                    ? "inline-flex items-center rounded-[var(--r-2)] border border-accent px-[5px] py-0.5 font-mono text-meta font-semibold text-accent-text"
                    : "font-mono text-meta text-ink-3"
                }
                title={`優先度${job.trigger.priority} — 小さい数字が先に走ります`}
              >
                P{job.trigger.priority}
              </span>
            </>
          )}
        </span>

        <span className="flex w-[200px] shrink-0 flex-col gap-0.5">
          <LastRunLine job={job} live={live} />
        </span>

        <span
          className={`w-[104px] shrink-0 text-secondary ${
            job.blocked ? "font-semibold text-accent-text" : "text-ink-2"
          }`}
        >
          {nextColumn(job)}
        </span>

        <span className="flex w-[92px] shrink-0 items-center justify-end gap-1.5">
          <RowButton onClick={running || queued ? onCancel : onRun}>{actionVerb(job)}</RowButton>
          <button
            type="button"
            onClick={onToggle}
            aria-label={open ? `${job.name} を折りたたむ` : `${job.name} を開く`}
            aria-expanded={open}
            className="text-secondary text-ink-3"
          >
            {open ? "⌃" : "⌄"}
          </button>
        </span>
      </div>

      {running && live && (
        <div className="flex items-center gap-3 px-4 pt-0 pb-[13px] pl-9">
          <span className="h-[5px] flex-1 overflow-hidden rounded-[var(--r-2)] bg-line-soft">
            <span
              className="block h-full rounded-[var(--r-2)] bg-accent"
              style={{ width: `${percent(live.current, live.total)}%` }}
            />
          </span>
          <span className="font-mono text-meta text-ink-3">
            {percent(live.current, live.total)}%
          </span>
          <span className="text-meta text-ink-4">
            {live.item ? `${live.item} — ${live.message ?? "処理中"}` : (live.message ?? "")}
          </span>
        </div>
      )}

      {failed && job.lastRun && (
        <FailureBody
          errors={errors}
          count={job.lastRun.errorCount}
          changed={job.lastRun.changed}
          retryable={job.retryable}
          onRetry={onRetry}
          onOpenCard={onOpenCard}
        />
      )}

      {open && !running && job.lastRun && (
        // Desktop's tally, which JOBS.md does not specify — it draws the card for the phone only.
        // Behind the chevron rather than in the row: the row is five fixed-width columns and the
        // breakdown is however many keys the job emits. `FailureBody` above is deliberately NOT
        // behind it, because a failure is news; a completion report is something you go and read.
        <div className="px-4 pb-3 pl-9">
          <TallyCard run={job.lastRun} />
        </div>
      )}

      {open && !running && (
        <ScheduleEditor
          key={job.id}
          trigger={job.trigger}
          blocked={job.blocked}
          sharingTime={jobsSharingTime(jobs, job)}
          onSave={onSaveTrigger}
          onError={onError}
        />
      )}

      {/* Below the schedule and INSIDE the same expanded block — no rule between them.
          A full-width hairline here reads as a row separator, and the settings then look like a
          header for the *next* job rather than the tail of this one. Seen in a browser, 9 Aug 2026;
          it is not something the markup suggests. Only for a job that declares any: an empty
          Settings block would be a control that has never done anything. */}
      {open && !running && job.settings.length > 0 && (
        <div className="flex flex-col gap-3 pr-4 pb-4 pl-9">
          <SettingsEditor
            key={job.id}
            settings={job.settings}
            values={job.settingValues}
            onSave={onSaveSettings}
            onError={onError}
          />
        </div>
      )}
    </div>
  );
}

function StateDot({ job }: { job: Job }) {
  // A job that has never run gets a hollow ring, not a colour: nothing has happened yet, and a
  // green dot would claim it had. A QUEUED job gets the same hollow shape — nothing has happened
  // yet there either — tinted towards the run it is about to have.
  const style =
    job.state === "running"
      ? "bg-accent"
      : job.state === "queued"
        ? "border-[1.5px] border-accent-line"
        : job.state === "failed"
          ? "bg-accent"
          : job.state === "passed"
            ? "bg-ink-3"
            : job.lastRun
              ? "bg-ink-off"
              : "border border-line-strong";
  return <span className={`size-2 shrink-0 rounded-full ${style}`} title={job.state} />;
}

function LastRunLine({ job, live }: { job: Job; live: JobProgress | null }) {
  if (job.state === "running") {
    return (
      <>
        <span className="text-[14px]/[1.3] font-semibold text-accent-text">
          {runningLabel(live?.current ?? 0, live?.total ?? 0)}
        </span>
        <span className="font-mono text-meta text-ink-4">
          {live ? elapsedLabel(live.elapsedSeconds, live.current, live.total) : "starting…"}
        </span>
      </>
    );
  }
  if (job.state === "queued") {
    // The same shape as every other state: a bold status line, a mono detail line under it. The
    // detail carries what the row would otherwise be showing — there is no estimated start time,
    // see `queuedDetail`.
    return (
      <>
        <span className="text-[14px]/[1.3] font-semibold text-ink-2">
          {queuedLabel(job.queuePosition)}
        </span>
        <span className="line-clamp-2 font-mono text-meta text-ink-4">{queuedDetail(job)}</span>
      </>
    );
  }
  if (!job.lastRun) return <span className="text-secondary text-ink-4">未実行</span>;

  const tone =
    job.state === "failed"
      ? "text-accent-text font-semibold"
      : job.state === "passed"
        ? "text-ink-3"
        : "text-ink-3";
  return (
    <>
      <span className={`text-[14px]/[1.3] ${tone}`}>{lastRunLabel(job.lastRun)}</span>
      {/* Clamped: the column is a fixed 200px and `fix-formatting` emits seven keys, which wrapped
          to four lines and pushed every other row down with it. The full breakdown is two lines
          below in the expanded row, as tiles — this is the glance. */}
      <span className="line-clamp-2 font-mono text-meta text-ink-4">{runSummary(job.lastRun)}</span>
    </>
  );
}

const ERRORS_SHOWN = 3;

function FailureBody({
  errors,
  count,
  changed,
  retryable,
  onRetry,
  onOpenCard,
}: {
  errors: JobError[];
  count: number;
  changed: number;
  retryable: boolean;
  onRetry: () => void;
  onOpenCard: (noteId: number) => void;
}) {
  const shown = errors.slice(0, ERRORS_SHOWN);
  const rest = count - shown.length;

  return (
    <div className="flex flex-col gap-2 px-4 pt-0 pb-[14px] pl-9">
      {/* Partial failure is the normal failure: say what was saved before saying what refused. */}
      <p className="text-secondary text-ink-2">
        {changed.toLocaleString()}枚は保存されました。下の{count.toLocaleString()}
        件はそのままです。
      </p>
      {shown.length > 0 && (
        <div className="overflow-hidden rounded-[var(--r-3)] border border-accent-border bg-panel">
          {shown.map((error, i) => (
            <div
              key={`${error.noteId}-${i}`}
              className="flex items-center gap-3 border-b border-line-soft px-3 py-2 last:border-b-0"
            >
              <span className="w-16 shrink-0 font-jp text-[17px]/[1.3] text-ink">
                {error.expression}
              </span>
              <span className="w-24 shrink-0 font-mono text-meta text-ink-3">{error.field}</span>
              <span className="min-w-0 flex-1 text-secondary text-ink-2">{error.reason}</span>
              {error.noteId !== null && (
                <button
                  type="button"
                  onClick={() => onOpenCard(error.noteId as number)}
                  className="shrink-0 text-meta text-accent-text hover:underline"
                >
                  カードを開く
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2.5">
        {retryable && (
          <button
            type="button"
            onClick={onRetry}
            className="rounded-[var(--r-2)] bg-accent px-[13px] py-2 text-[14px]/[1] font-semibold text-white"
          >
            失敗した{count.toLocaleString()}件を再試行
          </button>
        )}
        <button
          type="button"
          onClick={() =>
            void navigator.clipboard?.writeText(
              errors.map((e) => `${e.expression}\t${e.field}\t${e.reason}`).join("\n"),
            )
          }
          className="rounded-[var(--r-2)] border border-line-strong bg-panel px-[13px] py-2 text-[14px]/[1] font-semibold text-ink-2"
        >
          ログをコピー
        </button>
        {rest > 0 && (
          <span className="text-meta text-ink-3">
            他{rest.toLocaleString()}件のエラー
          </span>
        )}
      </div>
    </div>
  );
}

function RowButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="shrink-0 rounded-[var(--r-2)] border border-line-strong bg-panel px-[11px] py-[7px] text-[14px]/[1] font-semibold text-ink-2"
    >
      {children}
    </button>
  );
}
